// Phase 85.A.88 — Visitor tracking hook (login + guest).
//
// Mỗi visitor được gán `visitor_id` UUID lưu trong localStorage. Hook gọi
// POST /api/public/visit:
//   • Trên mỗi route change → event_type="pageview".
//   • Mỗi 30s khi tab active → event_type="heartbeat".
// Backend ghi nhận IP + UA + geo lookup, lưu vào collection `visits` với
// TTL 7 ngày (auto-cleanup).
//
// User choice: tracking guest ANONYMOUS — data tự xoá sau 7 ngày.
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { api } from "../lib/api";
import { jitter } from "../lib/edgeApi";
import { isEmbedRuntime } from "../lib/embedMode";

const STORAGE_KEY = "pickleague:visitor_id";
// Đợt 1 (08/2026) — 30s → 60s: giảm 50% POST /public/visit từ viewer
// (audit frontend-bundle-audit-2026-07-30.md mục 5).
const HEARTBEAT_INTERVAL_MS = 60_000;
// PR-E (GĐ2 audit) — dedupe heartbeat ĐA TAB: localStorage shared throttle.
// N tab cùng visitor → chỉ 1 tab bắn heartbeat mỗi chu kỳ (tab khác thấy
// mốc <45s thì skip). 45s < 60s để không bỏ lỡ chu kỳ khi tab chủ đóng.
const HB_SHARED_TS_KEY = "pl.visit.hb.ts";
const HB_SHARED_MIN_GAP_MS = 45_000;

function sharedHeartbeatAllowed() {
  try {
    const last = Number(window.localStorage.getItem(HB_SHARED_TS_KEY) || 0);
    if (Date.now() - last < HB_SHARED_MIN_GAP_MS) return false;
    window.localStorage.setItem(HB_SHARED_TS_KEY, String(Date.now()));
    return true;
  } catch {
    return true; // localStorage chặn → giữ hành vi cũ (per-tab)
  }
}

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "v-" + Math.random().toString(36).slice(2) + "-" + Date.now().toString(36);
}

function getVisitorId() {
  try {
    let v = window.localStorage.getItem(STORAGE_KEY);
    if (!v) {
      v = uuid();
      window.localStorage.setItem(STORAGE_KEY, v);
    }
    return v;
  } catch {
    // localStorage disabled — fallback in-memory id (sẽ regenerate mỗi reload).
    return uuid();
  }
}

async function postVisit(payload) {
  try {
    // v17.19 — Traffic control: tắt tracking / sampling 10% từ phía client.
    const { getTrafficFlag } = require("../lib/trafficFlags");
    if (!getTrafficFlag("visit_tracking")) return;
    if (getTrafficFlag("visit_sampling", false) && Math.random() >= 0.1) return;
    await api.post("/public/visit", payload);
  } catch {
    // Silent — visit tracking là best-effort.
  }
}

export default function useVisitorTracker() {
  const location = useLocation();
  const lastPathRef = useRef(null);
  const heartbeatRef = useRef(null);
  const pageviewTimerRef = useRef(null);

  // Page view trigger trên mỗi route change.
  // Đợt 1 (08/2026) — coalesce theo PATHNAME (bỏ ?search): đổi ?tab=/?match=
  // trong cùng trang không bắn pageview mới.
  // Đợt 1 HAR v16 (06/2026) — debounce 800ms: redirect canonical UUID→slug
  // (2 path trong <1s) chỉ ghi 1 pageview với path CUỐI CÙNG. 2 trang thật
  // (cách >800ms) vẫn ghi đủ 2. Backend còn lớp dedupe 3s làm lưới an toàn.
  useEffect(() => {
    if (isEmbedRuntime()) return undefined; // PR-A: không track trong iframe embed
    const path = location.pathname;
    if (path === lastPathRef.current) return undefined;
    lastPathRef.current = path;
    if (pageviewTimerRef.current) clearTimeout(pageviewTimerRef.current);
    pageviewTimerRef.current = setTimeout(() => {
      pageviewTimerRef.current = null;
      try {
        const { canRun } = require("../lib/networkGovernor");
        if (!canRun("visit")) return; // Đợt Offline-1 — không gửi, không gửi bù
      } catch { /* noop */ }
      const visitorId = getVisitorId();
      postVisit({ visitor_id: visitorId, path: lastPathRef.current, event_type: "pageview" });
    }, 800);
    return undefined;
  }, [location.pathname]);

  // Flush timer khi unmount app (hiếm — SPA root).
  useEffect(() => () => {
    if (pageviewTimerRef.current) clearTimeout(pageviewTimerRef.current);
  }, []);

  // Heartbeat 60s khi tab active (v17 P0 08/2026 — jitter ±20% chống N client
  // cùng nhịp đúng mốc :00/:30).
  useEffect(() => {
    if (isEmbedRuntime()) return undefined; // PR-A
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (typeof document === "undefined" || !document.hidden) {
        let allowed = true;
        try {
          const { canRun } = require("../lib/networkGovernor");
          allowed = canRun("visit");
        } catch { /* noop */ }
        if (allowed && sharedHeartbeatAllowed()) {
          const visitorId = getVisitorId();
          const path = lastPathRef.current || "/";
          postVisit({ visitor_id: visitorId, path, event_type: "heartbeat" });
        }
      }
      heartbeatRef.current = setTimeout(tick, jitter(HEARTBEAT_INTERVAL_MS));
    };
    heartbeatRef.current = setTimeout(tick, jitter(HEARTBEAT_INTERVAL_MS));
    return () => {
      cancelled = true;
      if (heartbeatRef.current) clearTimeout(heartbeatRef.current);
    };
  }, []);

  return null;
}
